/**
 * Guest Access administration surface.
 *
 * Admins pick a workspace and a guest user, then whitelist the epics/projects
 * that guest may see and toggle what they may change (tasks, action items,
 * checklists, comments, editing). Persisted via PUT /guest-access/.
 *
 * A guest with no configuration is locked down (sees nothing) by default.
 * @component
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import useCurrentUser from '../../utils/useCurrentUser';
import { useWorkspace } from '../../utils/WorkspaceContext';

const CAPABILITIES = [
  { key: 'can_manage_tasks', label: 'Add / remove tasks',
    help: 'Create and delete tasks in visible projects.' },
  { key: 'can_manage_action_items', label: 'Add / remove action items',
    help: 'Create and delete action items on visible tasks.' },
  { key: 'can_manage_checklists', label: 'Add checklists',
    help: 'Add and update checklist items on action items.' },
  { key: 'can_edit_items', label: 'Edit tasks & action items',
    help: 'Edit existing tasks and action items.' },
  { key: 'can_comment', label: 'Add comments',
    help: 'Comment on action items.' },
];

const EMPTY_CONFIG = {
  allowed_epics: [],
  allowed_projects: [],
  can_manage_tasks: false,
  can_manage_action_items: false,
  can_manage_checklists: false,
  can_edit_items: false,
  can_comment: true,
};

const GuestAccessSettings = () => {
  const { isAdmin, loading: userLoading } = useCurrentUser();
  const { workspaces, currentWorkspace } = useWorkspace();

  const [workspaceId, setWorkspaceId] = useState(currentWorkspace?.id || '');
  const [guests, setGuests] = useState([]);
  const [guestId, setGuestId] = useState('');
  const [epics, setEpics] = useState([]);
  const [projects, setProjects] = useState([]);
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Load the guest directory once.
  useEffect(() => {
    api
      .get('users/')
      .then((res) => setGuests(res.data.filter((u) => u.role === 'guest')))
      .catch(() => setToast({ severity: 'error', message: 'Could not load users.' }));
  }, []);

  // Load epics + projects for the selected workspace.
  useEffect(() => {
    if (!workspaceId) {
      setEpics([]);
      setProjects([]);
      return;
    }
    Promise.all([
      api.get('epics/', { params: { workspace_id: workspaceId } }),
      api.get('projects/', { params: { workspace_id: workspaceId } }),
    ])
      .then(([e, p]) => {
        setEpics(e.data || []);
        setProjects(p.data || []);
      })
      .catch(() => setToast({ severity: 'error', message: 'Could not load epics/projects.' }));
  }, [workspaceId]);

  // Load the selected guest's existing config for the workspace.
  useEffect(() => {
    if (!workspaceId || !guestId) {
      setConfig(EMPTY_CONFIG);
      return;
    }
    setLoading(true);
    api
      .get('guest-access/', { params: { workspace_id: workspaceId, user_id: guestId } })
      .then((res) => {
        const d = res.data || {};
        setConfig({
          allowed_epics: d.allowed_epics || [],
          allowed_projects: d.allowed_projects || [],
          can_manage_tasks: Boolean(d.can_manage_tasks),
          can_manage_action_items: Boolean(d.can_manage_action_items),
          can_manage_checklists: Boolean(d.can_manage_checklists),
          can_edit_items: Boolean(d.can_edit_items),
          can_comment: d.can_comment ?? true,
        });
      })
      .catch(() => setToast({ severity: 'error', message: 'Could not load guest access.' }))
      .finally(() => setLoading(false));
  }, [workspaceId, guestId]);

  const epicNameById = useMemo(
    () => Object.fromEntries(epics.map((e) => [e.id, e.name])), [epics]);
  const projectNameById = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);

  const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const save = async () => {
    if (!workspaceId || !guestId) return;
    setSaving(true);
    try {
      await api.put('guest-access/', {
        user: guestId,
        workspace: workspaceId,
        ...config,
      });
      setToast({ severity: 'success', message: 'Guest access saved.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to save guest access.';
      setToast({ severity: 'error', message: detail });
    } finally {
      setSaving(false);
    }
  };

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Alert severity="info">
        Only admins can configure guest access.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Guest Access
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose what a guest can see and do in a workspace. Guests start locked
          down until you grant access here.
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Workspace</InputLabel>
          <Select
            label="Workspace"
            value={workspaceId}
            onChange={(e) => { setWorkspaceId(e.target.value); setGuestId(''); }}
          >
            {workspaces.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!workspaceId}>
          <InputLabel>Guest</InputLabel>
          <Select
            label="Guest"
            value={guestId}
            onChange={(e) => setGuestId(e.target.value)}
          >
            {guests.length === 0 && (
              <MenuItem value="" disabled>No guest users</MenuItem>
            )}
            {guests.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.full_name || g.username} — {g.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        guestId && (
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Visible epics
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Selecting an epic also reveals all of its projects.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <Select
                multiple
                value={config.allowed_epics}
                onChange={(e) => setField('allowed_epics', e.target.value)}
                input={<OutlinedInput />}
                displayEmpty
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">None</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip key={id} size="small" label={epicNameById[id] || id} />
                      ))}
                    </Box>
                  )
                }
              >
                {epics.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    <Checkbox checked={config.allowed_epics.includes(e.id)} />
                    <ListItemText primary={e.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Additional visible projects
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <Select
                multiple
                value={config.allowed_projects}
                onChange={(e) => setField('allowed_projects', e.target.value)}
                input={<OutlinedInput />}
                displayEmpty
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">None</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip key={id} size="small" label={projectNameById[id] || id} />
                      ))}
                    </Box>
                  )
                }
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    <Checkbox checked={config.allowed_projects.includes(p.id)} />
                    <ListItemText primary={p.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              What this guest can do
            </Typography>
            <Stack sx={{ mb: 2 }}>
              {CAPABILITIES.map((cap) => (
                <FormControlLabel
                  key={cap.key}
                  control={
                    <Switch
                      checked={Boolean(config[cap.key])}
                      onChange={(e) => setField(cap.key, e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{cap.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cap.help}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
              ))}
            </Stack>

            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save access'}
            </Button>
          </Paper>
        )
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default GuestAccessSettings;
